import { useGoogleLogin } from '@react-oauth/google';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { usePostHog } from 'posthog-js/react';

import { useToast } from '@/hooks/useToast';
import { setCookie } from '@/lib/utils';
import { getUserProfileFromToken } from '@/lib/auth';
import { useUserProfile } from '@/contexts/user.context';
import {
  getAccessTokenFromGoogleAccessToken,
} from '@/lib/apis';

const useGoogleSignIn = () => {
  const posthog = usePostHog();

  const [isLoading, setIsLoading] = useState(false);
  const { setUser } = useUserProfile();
  const navigate = useNavigate();
  const { toast } = useToast();

  const signInWithGoogle = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setIsLoading(true);
      try {
        const response = await getAccessTokenFromGoogleAccessToken({ googleAccessToken: tokenResponse.access_token });

        if (response?.access_token) {
          setCookie('access_token', response.access_token, 1);
          const userProfile = getUserProfileFromToken(response.access_token);
          setUser(userProfile);
          const { name, email } = userProfile;
          posthog?.identify(email, {
            email,
            name,
          });
          posthog?.capture('login_completed', { method: 'google' });

          // Just navigate to a default authenticated route
          // The _authenticated.tsx will handle checking for pending invites
          navigate({ to: '/business' });
        } else {
          setIsLoading(false);
          toast({
            title: 'Login Failed',
            description: 'Unable to sign in due to an internal error. Please try again.',
          });
        }
      } catch (error) {
        setIsLoading(false);

        // Default message
        const errorMessage = error instanceof Error ? error.message : 'Unable to sign in due to an internal error. Please try again.';
        let description = errorMessage;

        // Specific message for "User Not Found"
        if (errorMessage.startsWith('User Not Found')) {
          description += ' You need to sign up first.';
        }

        toast({
          title: 'Login Failed',
          description,
        });
        if (errorMessage.startsWith('User Not Found'))
          navigate({ to: '/signin' });
      }
    },
  });

  return { signInWithGoogle, isLoading };
};

export default useGoogleSignIn;
